import React from 'react';
import { Calendar, Clock } from 'lucide-react';

/**
 * Компонент секции выбора периода для расчета рабочего времени
 */
const PeriodSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  // Получаем текущий год для ограничения выбора
  const currentYear = new Date().getFullYear();
  
  // Массив месяцев для выбора
  const months = [
    { value: 0, name: 'Январь' },
    { value: 1, name: 'Февраль' },
    { value: 2, name: 'Март' },
    { value: 3, name: 'Апрель' },
    { value: 4, name: 'Май' },
    { value: 5, name: 'Июнь' },
    { value: 6, name: 'Июль' },
    { value: 7, name: 'Август' },
    { value: 8, name: 'Сентябрь' },
    { value: 9, name: 'Октябрь' },
    { value: 10, name: 'Ноябрь' },
    { value: 11, name: 'Декабрь' }
  ];

  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calendar className="w-5 h-5 mr-2 text-cyan-500" />
        Период расчета
      </h3>
      
      {/* Тип периода */}
      <div className="mb-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-3 gap-2">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="periodType"
              value="month"
              checked={formData.periodType === 'month'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Месяц
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="periodType"
              value="year"
              checked={formData.periodType === 'year'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Год
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="periodType"
              value="custom"
              checked={formData.periodType === 'custom'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Произвольный период
            </span>
          </label>
        </div>
      </div>
      
      {/* Настройки для месяца */}
      {formData.periodType === 'month' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="month" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Месяц
            </label>
            <select
              id="month"
              name="month"
              value={formData.month}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Год
            </label>
            <select
              id="year"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              {[...Array(5)].map((_, i) => {
                const yearValue = currentYear - 2 + i;
                return (
                  <option key={yearValue} value={yearValue}>
                    {yearValue}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      )}
      
      {/* Настройки для года */}
      {formData.periodType === 'year' && (
        <div>
          <label htmlFor="yearOnly" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Год
          </label>
          <select
            id="yearOnly"
            name="year"
            value={formData.year}
            onChange={handleInputChange}
            className="form-select w-full md:w-1/2"
          >
            {[...Array(5)].map((_, i) => {
              const yearValue = currentYear - 2 + i;
              return (
                <option key={yearValue} value={yearValue}>
                  {yearValue}
                </option>
              );
            })}
          </select>
        </div>
      )}
      
      {/* Настройки для произвольного периода */}
      {formData.periodType === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Дата начала
            </label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.startDate ? 'input-error' : ''}`}
            />
            {errors.startDate && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.startDate}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Дата окончания
            </label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.endDate ? 'input-error' : ''}`}
            />
            {errors.endDate && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.endDate}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Подсказка по выбору периода */}
      <div className="mt-3 text-sm text-gray-500 dark:text-gray-400 flex items-start">
        <Clock className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-cyan-500 dark:text-cyan-400" />
        <p>
          {formData.periodType === 'month' && "Выберите месяц и год для расчета рабочего времени. Будут учтены все дни выбранного месяца."}
          {formData.periodType === 'year' && "Выберите год для расчета рабочего времени. Будут учтены все дни выбранного года включая праздники."}
          {formData.periodType === 'custom' && "Укажите начальную и конечную даты произвольного периода. Максимальная длительность периода - 1 год."}
        </p>
      </div>
    </div>
  );
};

export default PeriodSection;